/**
 * Field Report API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  FieldReport,
  FieldReportListParams,
  FieldReportListResponse,
  FieldReportStats,
  CreateFieldReportRequest,
  UpdateFieldReportRequest,
  ReviewFieldReportRequest,
  ApproveFieldReportRequest,
  RejectFieldReportRequest,
} from '../types/fieldReport';

/**
 * Field Report API Endpoints
 */
export const fieldReportApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // =========================================================================
    // LIST
    // =========================================================================

    getFieldReports: builder.query<
      ApiResponse<FieldReportListResponse>,
      { projectId: string; params?: FieldReportListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/field-reports`,
        params,
      }),
      providesTags: ['FieldReport'],
    }),

    // =========================================================================
    // STATS
    // =========================================================================

    getFieldReportStats: builder.query<
      ApiResponse<FieldReportStats>,
      { projectId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/field-reports/stats`,
      }),
      providesTags: ['FieldReportStats'],
    }),

    // =========================================================================
    // GET ONE
    // =========================================================================

    getFieldReport: builder.query<
      ApiResponse<FieldReport>,
      { projectId: string; reportId: string }
    >({
      query: ({ projectId, reportId }) => ({
        url: `/projects/${projectId}/field-reports/${reportId}`,
      }),
      providesTags: (_result, _error, { reportId }) => [{ type: 'FieldReport', id: reportId }],
    }),

    // =========================================================================
    // CREATE
    // =========================================================================

    createFieldReport: builder.mutation<
      ApiResponse<FieldReport>,
      { projectId: string; data: CreateFieldReportRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/field-reports`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['FieldReport', 'FieldReportStats'],
    }),

    // =========================================================================
    // UPDATE
    // =========================================================================

    updateFieldReport: builder.mutation<
      ApiResponse<FieldReport>,
      { projectId: string; reportId: string; data: UpdateFieldReportRequest }
    >({
      query: ({ projectId, reportId, data }) => ({
        url: `/projects/${projectId}/field-reports/${reportId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        'FieldReport',
        { type: 'FieldReport', id: reportId },
        'FieldReportStats',
      ],
    }),

    // =========================================================================
    // DELETE
    // =========================================================================

    deleteFieldReport: builder.mutation<
      ApiResponse<void>,
      { projectId: string; reportId: string }
    >({
      query: ({ projectId, reportId }) => ({
        url: `/projects/${projectId}/field-reports/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FieldReport', 'FieldReportStats'],
    }),

    // =========================================================================
    // WORKFLOW
    // =========================================================================

    submitFieldReport: builder.mutation<
      ApiResponse<FieldReport>,
      { projectId: string; reportId: string }
    >({
      query: ({ projectId, reportId }) => ({
        url: `/projects/${projectId}/field-reports/${reportId}/submit`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        'FieldReport',
        { type: 'FieldReport', id: reportId },
        'FieldReportStats',
      ],
    }),

    reviewFieldReport: builder.mutation<
      ApiResponse<FieldReport>,
      { projectId: string; reportId: string; data: ReviewFieldReportRequest }
    >({
      query: ({ projectId, reportId, data }) => ({
        url: `/projects/${projectId}/field-reports/${reportId}/review`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        'FieldReport',
        { type: 'FieldReport', id: reportId },
        'FieldReportStats',
      ],
    }),

    approveFieldReport: builder.mutation<
      ApiResponse<FieldReport>,
      { projectId: string; reportId: string; data: ApproveFieldReportRequest }
    >({
      query: ({ projectId, reportId, data }) => ({
        url: `/projects/${projectId}/field-reports/${reportId}/approve`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        'FieldReport',
        { type: 'FieldReport', id: reportId },
        'FieldReportStats',
      ],
    }),

    rejectFieldReport: builder.mutation<
      ApiResponse<FieldReport>,
      { projectId: string; reportId: string; data: RejectFieldReportRequest }
    >({
      query: ({ projectId, reportId, data }) => ({
        url: `/projects/${projectId}/field-reports/${reportId}/reject`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        'FieldReport',
        { type: 'FieldReport', id: reportId },
        'FieldReportStats',
      ],
    }),
  }),
});

export const {
  useGetFieldReportsQuery,
  useGetFieldReportStatsQuery,
  useGetFieldReportQuery,
  useCreateFieldReportMutation,
  useUpdateFieldReportMutation,
  useDeleteFieldReportMutation,
  useSubmitFieldReportMutation,
  useReviewFieldReportMutation,
  useApproveFieldReportMutation,
  useRejectFieldReportMutation,
} = fieldReportApi;
